import { useT } from '../i18n';
import { useToasts } from '../ui/toasts';

/*
    The undo shelf.

    Bottom left, out of the way of the reading pane and clear of the composer, which sits bottom
    right. It is announced to screen readers as a status rather than an alert: something has
    happened and there is a way back, but nothing is wrong and nothing demands attention.

    Nothing here is modal. The message list stays usable underneath, and ignoring the shelf is a
    perfectly good way to accept what happened.
*/
export default function Toasts() {
    const { t } = useT();
    const toasts = useToasts((s) => s.toasts);
    const dismiss = useToasts((s) => s.dismiss);

    if (!toasts.length) return null;

    return (
        <div
            data-testid="toasts"
            data-print-hide
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                left: 'var(--sp-7)',
                bottom: 'var(--sp-7)',
                zIndex: 70,
                display: 'grid',
                gap: 'var(--sp-4)',
                // Never wider than a phone, never wider than the screen.
                maxWidth: 'min(420px, calc(100vw - 2 * var(--sp-7)))',
            }}
        >
            {toasts.map((toast) => (
                <div
                    key={toast.id}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-6)',
                        padding: 'var(--sp-5) var(--sp-6)',
                        background: 'var(--surface)',
                        border: '1px solid var(--border2)',
                        borderRadius: 'var(--r-btn)',
                        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
                        fontSize: 'var(--fs-md)',
                        color: 'var(--text)',
                    }}
                >
                    <span style={{ flex: 1, minWidth: 0 }}>
                        {t(toast.message as Parameters<typeof t>[0], toast.values)}
                    </span>
                    {toast.undo ? (
                        <button
                            data-testid="toast-undo"
                            onClick={() => {
                                toast.undo?.();
                                dismiss(toast.id);
                            }}
                            style={{
                                flex: '0 0 auto',
                                // 44 high: this is the one control in the interface somebody
                                // reaches for in a hurry, often on a phone, often having just
                                // made a mistake.
                                minHeight: 44,
                                padding: '0 var(--sp-5)',
                                background: 'transparent',
                                border: '1px solid var(--accent)',
                                borderRadius: 'var(--r-btn)',
                                color: 'var(--accent-strong)',
                                fontSize: 'var(--fs-md)',
                                fontWeight: 600,
                            }}
                        >
                            {t('action.undo')}
                        </button>
                    ) : null}
                </div>
            ))}
        </div>
    );
}
